"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import ChartLineIcon from "@/app/assets/icons/why-choose-us/chart-line";
import {
    WHY_CHOOSE_CATEGORIES,
    isWhyChooseVideo,
    type WhyChooseCategoryId,
} from "@/app/components/icm 3.0/why-choose-us-data";

const STOCKS = WHY_CHOOSE_CATEGORIES[0];
const DEFAULT_CATEGORY: WhyChooseCategoryId = "stocks";
const DEFAULT_FEATURE = STOCKS.features?.[0]?.label ?? "Save Brokerage";

/** Figma 5092:31363 track / 5092:31364 thumb */
const RAIL_HEIGHT = 625;
const RAIL_THUMB_HEIGHT = 113;
const RAIL_TRAVEL = RAIL_HEIGHT - RAIL_THUMB_HEIGHT;

/**
 * Discrete thumb position along the rail (not playback %).
 * 4 clips → 0, ~33.33%, ~66.67%, 100%.
 */
function getRailThumbProgress(stepIndex: number, stepCount: number): number {
    if (stepCount <= 1) return 0;
    const clamped = Math.min(Math.max(stepIndex, 0), stepCount - 1);
    return clamped / (stepCount - 1);
}

/** Flat selectable steps — drives the left progress thumb. */
function buildProgressSteps() {
    const steps: { categoryId: WhyChooseCategoryId; featureLabel?: string }[] =
        [];
    for (const cat of WHY_CHOOSE_CATEGORIES) {
        if (cat.features?.length) {
            for (const feature of cat.features) {
                steps.push({
                    categoryId: cat.id,
                    featureLabel: feature.label,
                });
            }
        } else {
            steps.push({ categoryId: cat.id });
        }
    }
    return steps;
}

const PROGRESS_STEPS = buildProgressSteps();

function getProgressIndex(
    categoryId: WhyChooseCategoryId,
    featureLabel: string,
): number {
    const idx = PROGRESS_STEPS.findIndex(
        (step) =>
            step.categoryId === categoryId &&
            (step.featureLabel === undefined ||
                step.featureLabel === featureLabel),
    );
    return Math.max(0, idx);
}

/**
 * Vertical position indicator — Figma 5092:31363
 * Track: 3×625, white/10, rounded 8px
 * Thumb: 3×113, white/25 — snaps to active step (not video scrub progress)
 */
const VerticalProgressRail = ({
    progress,
}: {
    /** 0 → 1, discrete step position */
    progress: number;
}) => {
    const thumbTop = Math.min(
        Math.max(progress, 0) * RAIL_TRAVEL,
        RAIL_TRAVEL,
    );

    return (
        <div
            aria-hidden
            className="relative h-[625px] w-[3px] shrink-0 overflow-hidden rounded-[8px] bg-[rgba(255,255,255,0.1)]"
        >
            <div
                className="absolute left-1/2 w-[3px] -translate-x-1/2 rounded-[8px] bg-[rgba(255,255,255,0.25)] transition-[top] duration-300 ease-out"
                style={{ height: RAIL_THUMB_HEIGHT, top: thumbTop }}
            />
        </div>
    );
};

/**
 * Why Choose Us — Figma desktop 5092:31326 / mobile 2541:30594
 * Click to select category / nested feature; media panel updates to match.
 * Left rail (5092:31363) tracks selection progress through the nested list.
 * Videos auto-advance on `ended` through PROGRESS_STEPS (loops after last)
 * on both desktop and mobile.
 */
const WhyChooseUs = () => {
    const [activeCategory, setActiveCategory] =
        useState<WhyChooseCategoryId>(DEFAULT_CATEGORY);
    const [activeFeature, setActiveFeature] = useState(DEFAULT_FEATURE);
    const navRef = useRef<HTMLElement>(null);
    const activeItemRef = useRef<HTMLLIElement>(null);
    const activeMobileTabRef = useRef<HTMLButtonElement>(null);
    const desktopVideoRef = useRef<HTMLVideoElement>(null);
    const mobileVideoRef = useRef<HTMLVideoElement>(null);
    /** Bumps on every selection so stale `ended` handlers cannot advance. */
    const selectionIdRef = useRef(0);
    const progressIndexRef = useRef(0);
    const activeCategoryRef = useRef(activeCategory);
    const activeFeatureRef = useRef(activeFeature);
    const selectProgressStepRef = useRef<(index: number) => void>(() => {});

    const activeIndex = Math.max(
        0,
        WHY_CHOOSE_CATEGORIES.findIndex((c) => c.id === activeCategory),
    );
    const activeMeta = WHY_CHOOSE_CATEGORIES[activeIndex] ?? STOCKS;

    const progressIndex = getProgressIndex(activeCategory, activeFeature);
    progressIndexRef.current = progressIndex;
    activeCategoryRef.current = activeCategory;
    activeFeatureRef.current = activeFeature;
    const railProgress = getRailThumbProgress(
        progressIndex,
        PROGRESS_STEPS.length,
    );

    const activeMedia =
        activeMeta.features?.find((f) => f.label === activeFeature)?.media ??
        activeMeta.media;
    const activeVideoSrc = isWhyChooseVideo(activeMedia)
        ? activeMedia.src
        : null;

    /** Single writer for active point — used by clicks and auto-advance. */
    const selectProgressStep = (index: number) => {
        const len = PROGRESS_STEPS.length;
        if (len === 0) return;
        const nextIndex = ((index % len) + len) % len;
        const step = PROGRESS_STEPS[nextIndex];
        if (!step) return;

        const alreadyActive =
            step.categoryId === activeCategoryRef.current &&
            (step.featureLabel === undefined ||
                step.featureLabel === activeFeatureRef.current);
        if (alreadyActive) return;

        selectionIdRef.current += 1;
        setActiveCategory(step.categoryId);
        if (step.featureLabel) {
            setActiveFeature(step.featureLabel);
        } else {
            const cat = WHY_CHOOSE_CATEGORIES.find(
                (c) => c.id === step.categoryId,
            );
            if (cat?.features?.[0]) {
                setActiveFeature(cat.features[0].label);
            }
        }
    };
    selectProgressStepRef.current = selectProgressStep;

    const selectCategory = (id: WhyChooseCategoryId) => {
        const firstStepIndex = PROGRESS_STEPS.findIndex(
            (step) => step.categoryId === id,
        );
        if (firstStepIndex >= 0) {
            selectProgressStep(firstStepIndex);
            return;
        }
        selectionIdRef.current += 1;
        setActiveCategory(id);
        const cat = WHY_CHOOSE_CATEGORIES.find((c) => c.id === id);
        if (cat?.features?.[0]) {
            setActiveFeature(cat.features[0].label);
        }
    };

    const selectFeature = (label: string) => {
        const stepIndex = PROGRESS_STEPS.findIndex(
            (step) =>
                step.categoryId === activeCategoryRef.current &&
                step.featureLabel === label,
        );
        if (stepIndex >= 0) {
            selectProgressStep(stepIndex);
            return;
        }
        selectionIdRef.current += 1;
        setActiveFeature(label);
    };

    // Keep the active category visible inside the clipped nav (pairs with rail thumb)
    useEffect(() => {
        const nav = navRef.current;
        const item = activeItemRef.current;
        if (!nav || !item) return;
        const navRect = nav.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        const fadeReserve = 120;
        const visibleBottom = navRect.bottom - fadeReserve;
        if (itemRect.top < navRect.top || itemRect.bottom > visibleBottom) {
            item.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
    }, [activeCategory, activeFeature]);

    // Keep the active mobile tab in view while auto-advancing
    useEffect(() => {
        activeMobileTabRef.current?.scrollIntoView({
            inline: "nearest",
            block: "nearest",
            behavior: "smooth",
        });
    }, [activeCategory]);

    // Play the visible clip once; on `ended`, advance (desktop + mobile share PROGRESS_STEPS).
    useEffect(() => {
        if (!activeVideoSrc) return;

        const mq = window.matchMedia("(min-width: 1280px)"); // project `md` breakpoint

        const bindPrimary = () => {
            const isDesktop = mq.matches;
            const primary = isDesktop
                ? desktopVideoRef.current
                : mobileVideoRef.current;
            const secondary = isDesktop
                ? mobileVideoRef.current
                : desktopVideoRef.current;

            if (secondary) {
                secondary.pause();
            }
            if (!primary) return () => {};

            const boundSelectionId = selectionIdRef.current;

            const handleEnded = () => {
                if (selectionIdRef.current !== boundSelectionId) return;
                selectProgressStepRef.current(progressIndexRef.current + 1);
            };

            const tryPlay = () => {
                if (selectionIdRef.current !== boundSelectionId) return;
                void primary.play().catch(() => {
                    /* autoplay may be blocked; muted + playsInline should allow it */
                });
            };

            primary.addEventListener("ended", handleEnded);

            if (primary.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
                tryPlay();
            } else {
                primary.addEventListener("loadeddata", tryPlay, { once: true });
            }

            return () => {
                primary.removeEventListener("ended", handleEnded);
                primary.removeEventListener("loadeddata", tryPlay);
                primary.pause();
            };
        };

        let cleanup = bindPrimary();
        const onBreakpointChange = () => {
            cleanup?.();
            cleanup = bindPrimary();
        };
        mq.addEventListener("change", onBreakpointChange);

        return () => {
            mq.removeEventListener("change", onBreakpointChange);
            cleanup?.();
        };
    }, [activeVideoSrc]);

    return (
        <section
            aria-labelledby="why-choose-us-heading"
            className="w-full overflow-hidden rounded-t-[20px] bg-nav-bg md:rounded-t-[50px]"
        >
            <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-10 px-6 pb-10 pt-10 md:gap-[54px] md:px-0 md:pb-[60px] md:pt-[60px]">
                <header className="flex w-full flex-col items-center gap-2 text-center">
                    <p className="w-full text-base font-semibold uppercase leading-[22px] tracking-[2px] text-[#F47843] md:text-xl md:leading-[26px]">
                        WHY CHOOSE US?
                    </p>
                    <h2
                        id="why-choose-us-heading"
                        className="w-full text-2xl font-bold leading-8 text-white md:text-[48px] md:leading-[58px]"
                    >
                        The InCred Money Advantage
                    </h2>
                </header>

                {/* ── Mobile: horizontal tabs + panel ── */}
                <div className="flex w-full flex-col gap-10 md:hidden">
                    <div
                        role="tablist"
                        aria-label="Product categories"
                        className="-mx-6 flex gap-2 overflow-x-auto px-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    >
                        {WHY_CHOOSE_CATEGORIES.map((cat) => {
                            const isActive = cat.id === activeCategory;
                            return (
                                <button
                                    key={cat.id}
                                    ref={isActive ? activeMobileTabRef : undefined}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    id={`why-choose-tab-${cat.id}`}
                                    onClick={() => selectCategory(cat.id)}
                                    className={cn(
                                        "relative shrink-0 whitespace-nowrap px-2.5 py-1.5 text-base leading-[22px] transition-colors",
                                        isActive
                                            ? "font-semibold text-white"
                                            : "font-regular text-[#A5A5A5]",
                                    )}
                                >
                                    {cat.label}
                                    {isActive ? (
                                        <span
                                            aria-hidden
                                            className="absolute inset-x-0 bottom-0 h-0.5 bg-white"
                                        />
                                    ) : null}
                                </button>
                            );
                        })}
                    </div>

                    <div
                        role="tabpanel"
                        aria-labelledby={`why-choose-tab-${activeCategory}`}
                        className="relative h-[492px] w-full overflow-hidden rounded-[24px] bg-nav-fg"
                    >
                        {isWhyChooseVideo(activeMedia) ? (
                            <video
                                key={activeMedia.src}
                                ref={mobileVideoRef}
                                src={activeMedia.src}
                                autoPlay
                                muted
                                playsInline
                                className="h-full w-full object-cover"
                                aria-hidden
                            />
                        ) : (
                            <Image
                                key={
                                    activeMeta.features
                                        ? `${activeCategory}-${activeFeature}`
                                        : activeCategory
                                }
                                src={activeMedia.src}
                                alt=""
                                fill
                                className="object-cover"
                                unoptimized
                                sizes="(max-width: 1279px) 100vw, 345px"
                                aria-hidden
                            />
                        )}
                        <span className="sr-only">
                            {activeMeta.label}
                            {activeMeta.features ? ` — ${activeFeature}` : ""}{" "}
                            preview
                        </span>
                    </div>
                </div>

                {/* ── Desktop: rail + nav (offset) + panel — Figma 5092:31326 ── */}
                <div className="relative hidden w-full md:flex justify-between">
                    {/* Rail + nav sit 60px below panel top (266 − 206) */}
                    <div className="mt-[60px] flex shrink-0 gap-[33px]">
                        {/* Figma 5092:31363 — vertical progress beside the list */}
                        <VerticalProgressRail progress={railProgress} />

                        <nav
                            ref={navRef}
                            aria-label="Product categories"
                            className="relative h-[625px] w-full shrink-0 overflow-y-auto overflow-x-hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                        >
                            <ul className="m-0 flex list-none flex-col gap-10 p-0 pb-[120px]">
                                {WHY_CHOOSE_CATEGORIES.map((cat) => {
                                    const isActive = cat.id === activeCategory;
                                    const showFeatures = Boolean(
                                        isActive && cat.features?.length,
                                    );

                                    return (
                                        <li
                                            key={cat.id}
                                            ref={
                                                isActive
                                                    ? activeItemRef
                                                    : undefined
                                            }
                                            className="flex flex-col gap-3"
                                        >
                                            <button
                                                type="button"
                                                aria-current={
                                                    isActive
                                                        ? "true"
                                                        : undefined
                                                }
                                                onClick={() =>
                                                    selectCategory(cat.id)
                                                }
                                                className={cn(
                                                    "flex w-full flex-col items-start gap-3 text-left transition-colors",
                                                    isActive
                                                        ? "text-white"
                                                        : "text-[rgba(255,255,255,0.25)]",
                                                )}
                                            >
                                                {cat.id === "stocks" &&
                                                isActive ? (
                                                    <ChartLineIcon className="text-white" />
                                                ) : null}
                                                <span
                                                    className={cn(
                                                        "text-[30px] leading-9",
                                                        isActive
                                                            ? "font-bold"
                                                            : "font-medium",
                                                    )}
                                                >
                                                    {cat.labelLines
                                                        ? cat.labelLines.map(
                                                              (line) => (
                                                                  <span
                                                                      key={line}
                                                                      className="block"
                                                                  >
                                                                      {line}
                                                                  </span>
                                                              ),
                                                          )
                                                        : cat.label}
                                                </span>
                                            </button>

                                            {showFeatures && cat.features ? (
                                                <ul className="flex list-disc flex-col gap-3 pl-[36px]">
                                                    {cat.features.map(
                                                        (feature) => {
                                                            const featureActive =
                                                                feature.label ===
                                                                activeFeature;
                                                            return (
                                                                <li
                                                                    key={
                                                                        feature.id
                                                                    }
                                                                    className={cn(
                                                                        "text-2xl font-medium leading-8 transition-colors",
                                                                        featureActive
                                                                            ? "text-white"
                                                                            : "text-[rgba(255,255,255,0.25)]",
                                                                    )}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        aria-current={
                                                                            featureActive
                                                                                ? "true"
                                                                                : undefined
                                                                        }
                                                                        onClick={() =>
                                                                            selectFeature(
                                                                                feature.label,
                                                                            )
                                                                        }
                                                                        className={cn(
                                                                            "text-left text-2xl font-medium leading-8 transition-colors",
                                                                            featureActive
                                                                                ? "text-white"
                                                                                : "text-[rgba(255,255,255,0.25)]",
                                                                        )}
                                                                    >
                                                                        {
                                                                            feature.label
                                                                        }
                                                                    </button>
                                                                </li>
                                                            );
                                                        },
                                                    )}
                                                </ul>
                                            ) : null}
                                        </li>
                                    );
                                })}
                            </ul>
                            {/* Bottom fade — Figma 5092:31365 */}
                            <div
                                aria-hidden
                                className="pointer-events-none absolute inset-x-0 bottom-0 h-[120px] bg-gradient-to-t from-nav-bg to-transparent"
                            />
                        </nav>
                    </div>

                    <div
                        role="region"
                        aria-live="polite"
                        aria-label={`${activeMeta.label}${
                            activeMeta.features ? ` — ${activeFeature}` : ""
                        } details`}
                        className="relative h-[699px] min-w-0 max-w-[800px] flex-1 overflow-hidden rounded-[24px] bg-nav-fg"
                    >
                        {isWhyChooseVideo(activeMedia) ? (
                            <video
                                key={activeMedia.src}
                                ref={desktopVideoRef}
                                src={activeMedia.src}
                                autoPlay
                                muted
                                playsInline
                                className="h-full w-full object-cover"
                                aria-hidden
                            />
                        ) : (
                            <Image
                                key={
                                    activeMeta.features
                                        ? `${activeCategory}-${activeFeature}`
                                        : activeCategory
                                }
                                src={activeMedia.src}
                                alt=""
                                fill
                                className="object-cover"
                                unoptimized
                                sizes="902px"
                                priority
                                aria-hidden
                            />
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default WhyChooseUs;
